/**
 * Client-side Analytics Tracking Utility
 * Batches and sends events to the analytics API
 */

import type { AnalyticsEvent } from './clickhouse';
import { memoryStats } from './tab-crash-sentinel';

/**
 * What the page holds at a load-more, as event properties. A deep feed that
 * keeps growing shows up here as a rising curve, long before a tab dies of it.
 */
function loadMoreMemoryProperties(): Record<string, string> {
  return Object.fromEntries(Object.entries(memoryStats()).map(([k, v]) => [k, String(v)]));
}
import { isSessionSampled } from './impression-sampling';
import { configureImpressionQueue, flushImpressions } from './impression-queue';
import { isAnalyticsDisabled } from './analytics-enabled';
import { trackError } from './error-tracking';
import { getRankingVariant } from './ranking-variant-store';
import { getAnonymousId, getSessionId, startNewSession } from './tracking-context';
import { bridgeLegacyIdentity, linkUserToBrowser, linkWidgetToBrowser } from './identity-bridge';
import type { ErrorContext } from './schemas/error-tracking';
import type { EmailClick } from './email-click';
import { UI_TEST_EXPOSURE_EVENT } from '../schemas/ui-tests';
import type { ListingPageType } from './listing-page-type';

// Batch configuration
const BATCH_SIZE = 3; // Reduced for easier testing
const BATCH_INTERVAL_MS = 2000; // Send batch every 2 seconds (faster for testing)
const API_ENDPOINT = '/api/analytics/track';

/**
 * Built only by getAnalytics() and the `analytics` export below, and both
 * answer null without a window -- so every method here runs in a browser and
 * needs no server guard of its own.
 */
class AnalyticsTracker {
  private eventQueue: AnalyticsEvent[] = [];
  private batchTimer: NodeJS.Timeout | null = null;

  constructor() {
    // The impression queue must sample off the SAME session id the click events
    // carry, or the two sides of the click-through ratio disagree.
    configureImpressionQueue(getSessionId(), getAnonymousId());

    /**
     * Report this browser's retired anonymous ids, once, so its history before
     * 2026-09-16 can be attached to it. See utils/identity-bridge.ts -- the
     * browser is the only party that ever held all three ids, and every
     * browser that clears storage takes its mapping with it, so this is worth
     * doing on the first load after the consolidation rather than never.
     *
     * Deferred to a macrotask: it reads localStorage and may queue an event,
     * and neither belongs on the path to first paint.
     */
    setTimeout(() => {
      try {
        bridgeLegacyIdentity();
      } catch {
        // Recovering old identity is a nice-to-have; it must never be able to
        // take down the analytics tracker that every page mounts.
      }
      try {
        // A click through from a publisher widget: pair its reader id with ours.
        linkWidgetToBrowser();
      } catch {
        // Same rule: identity linking never takes the tracker down.
      }
    }, 0);

    // Send any queued events before page unload
    window.addEventListener('beforeunload', () => {
      // `void`, matching the other two call sites: flush() is async, and an
      // unload handler cannot await anything anyway -- the send is a beacon.
      // Without it this was the repo's one lint ERROR, which is what let 423
      // warnings accumulate behind a red `npm run lint` nobody could read.
      void this.flush(true);
      flushImpressions();
    });
  }

  /**
   * Is this session in the impression sample? Decided from the session id, so a
   * session contributes all of its impressions AND all of its clicks -- nothing
   * is half-counted.
   */
  isSampled(): boolean {
    return isSessionSampled(getSessionId());
  }

  /**
   * Get base event properties
   */
  private getBaseEvent(): Partial<AnalyticsEvent> {
    // Determine environment from Vercel env variable or fallback to NODE_ENV
    const getEnvironment = (): string => {
      // NEXT_PUBLIC_VERCEL_ENV is 'production', 'preview', or 'development'
      const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
      if (vercelEnv === 'preview') {
        return 'staging';
      }
      return vercelEnv || process.env.NODE_ENV || 'development';
    };

    // Get user_id from localStorage if logged in
    const getUserId = (): number | undefined => {
      try {
        const stored = localStorage.getItem('sw_user_id');
        return stored ? parseInt(stored, 10) : undefined;
      } catch {
        return undefined;
      }
    };

    const userId = getUserId();

    // Read per event, not cached on the instance, and for the same reason the
    // ranking variant is: this class is constructed once per page, while the
    // session can legitimately roll over mid-visit after 30 idle minutes. A
    // cached copy is how one module keeps writing an id the others have already
    // retired -- the exact drift this identity was consolidated to end. Cheap:
    // both are a sessionStorage read behind a same-value cookie guard.
    const sessionId = getSessionId();
    const anonymousId = getAnonymousId();

    // Keep the impression queue on whatever the current session is, so the two
    // sides of the click-through ratio cannot end up on different ids.
    configureImpressionQueue(sessionId, anonymousId);

    return {
      session_id: sessionId,
      anonymous_id: anonymousId,
      ...(userId && { user_id: userId }), // Include user_id if logged in
      // Which ranking arm served this visit. `control` until a showcase
      // response says otherwise, never '' -- an empty label means "predates the
      // column" and every experiment query excludes it, so leaving it unset is
      // how `events` ended up with 19,867 unlabelled rows a day and an
      // experiment mechanism that could not measure anything.
      //
      // Read per event rather than cached on the instance: the server can move
      // a visitor's arm mid-visit, and this class is constructed once per page.
      ranking_variant: getRankingVariant(),
      page_url: window.location.href,
      referrer: document.referrer || '',
      user_agent: navigator.userAgent,
      environment: getEnvironment(),
      timestamp: new Date(),
    };
  }

  /**
   * Sanitize event data to remove circular references and non-serializable values
   */
  private sanitizeEventData(obj: unknown): unknown {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeEventData(item));
    }

    // Handle plain objects - only keep serializable values
    const record = obj as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};
    for (const key in record) {
      if (!Object.prototype.hasOwnProperty.call(record, key)) continue;

      const value = record[key];

      // Skip functions, DOM elements, and React elements
      if (typeof value === 'function') continue;
      if (value instanceof Element) continue;
      if (value && typeof value === 'object' && (value as Record<string, unknown>).$$typeof) continue; // React element

      // A Date has no own keys, so the recursion below turned every event's
      // `timestamp` into `{}` and the server stamped arrival time instead: two
      // load-mores in one batch landed on the same millisecond.
      if (value instanceof Date) {
        if (!Number.isNaN(value.getTime())) sanitized[key] = value.toISOString();
        continue;
      }

      // Recursively sanitize nested objects
      if (typeof value === 'object' && value !== null) {
        try {
          // Test if it can be stringified
          JSON.stringify(value);
          sanitized[key] = this.sanitizeEventData(value);
        } catch (_e) {
          // Skip circular references
        }
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Track a generic event
   */
  track(event: Partial<AnalyticsEvent>): void {
    if (isAnalyticsDisabled()) return;

    const fullEvent = {
      ...this.getBaseEvent(),
      ...event,
      ip_address: '0.0.0.0', // Will be set server-side
    } as AnalyticsEvent;

    // Sanitize the event to remove circular references
    const sanitizedEvent = this.sanitizeEventData(fullEvent) as AnalyticsEvent;

    this.eventQueue.push(sanitizedEvent);

    // Auto-flush if batch size reached
    if (this.eventQueue.length >= BATCH_SIZE) {
      void this.flush();
    } else {
      this.scheduleBatch();
    }
  }

  /**
   * Track page view
   */
  pageView(pageLoadTime?: number): void {
    this.track({
      event_type: 'page_view',
      page_load_time: pageLoadTime,
    });
  }

  /**
   * A landing from one of our emails. See utils/email-click.ts.
   */
  emailClick(click: EmailClick): void {
    const test = click.uiTest;
    this.track({
      event_type: 'email_click',
      properties: { template: click.template, path: click.path, ...(test ? { arm: test.arm, run: test.run } : {}) },
    });
    // An email test's person is the one who clicked: the send had no browser to
    // count. Same row as a web exposure, so utils/ui-tests/results.ts reads it.
    if (test) {
      this.track({
        event_type: UI_TEST_EXPOSURE_EVENT,
        properties: { test: test.test, run: test.run, arm: test.arm, phase: test.phase, source: 'email' },
      });
    }
  }

  /**
   * Track listing view
   */
  listingView(listing: {
    id: number;
    title?: string;
    price?: number;
    categoryId?: number;
    categoryGroupId?: number;
    tags?: string[];
    /**
     * Where the click that led here happened. Absent for a direct hit, a
     * bookmark or an inbound link -- those genuinely have no list context, and
     * a null position is the honest answer rather than a defaulted one.
     */
    resultPosition?: number;
    totalResults?: number;
    listName?: string;
    rowIndex?: number;
    pageType?: ListingPageType;
    searchQuery?: string;
    activeFilters?: Record<string, string>;
    randomized?: boolean;
  }): void {
    this.track({
      event_type: 'listing_view',
      listing_id: listing.id,
      listing_title: listing.title,
      listing_price: listing.price,
      category_id: listing.categoryId,
      category_group_id: listing.categoryGroupId,
      tags: listing.tags,
      result_position: listing.resultPosition,
      total_results: listing.totalResults,
      list_name: listing.listName,
      row_index: listing.rowIndex,
      page_type: listing.pageType,
      search_query: listing.searchQuery,
      active_filters: listing.activeFilters,
      randomized: listing.randomized ? 1 : 0,
      // Both sides of the click-through ratio must agree on the sample, or the
      // rate is inflated by 1/rate with no way to correct it at query time.
      sampled: this.isSampled() ? 1 : 0,
    });
  }

  /**
   * Track listing hover (with throttling via useListingHover hook)
   */
  listingHover(listing: {
    id: number;
    title?: string;
    price?: number;
    categoryId?: number;
    categoryGroupId?: number;
    resultPosition?: number;
    totalResults?: number;
    searchQuery?: string;
    pageType?: ListingPageType;
    activeFilters?: Record<string, string>;
    tags?: string[];
    listName?: string;
    rowIndex?: number;
  }): void {
    this.track({
      event_type: 'listing_hover',
      listing_id: listing.id,
      listing_title: listing.title,
      listing_price: listing.price,
      category_id: listing.categoryId,
      category_group_id: listing.categoryGroupId,
      result_position: listing.resultPosition,
      total_results: listing.totalResults,
      search_query: listing.searchQuery,
      page_type: listing.pageType,
      active_filters: listing.activeFilters,
      tags: listing.tags,
      list_name: listing.listName,
      row_index: listing.rowIndex,
      sampled: this.isSampled() ? 1 : 0,
    });
  }

  /**
   * Track load more click
   * Tracks when user clicks to load more content (listings, showcases, recommendations)
   */
  loadMore(context: {
    pageType?: ListingPageType;
    contentType?: 'listings' | 'showcases' | 'recommendations';
    searchQuery?: string;
    categoryId?: number;
    categoryGroupId?: number;
    activeFilters?: Record<string, string>;
    currentPage?: number;
    itemsLoaded?: number;
    totalItemsAvailable?: number;
  }): void {
    this.track({
      event_type: 'load_more',
      page_type: context.pageType,
      content_type: context.contentType,
      search_query: context.searchQuery,
      category_id: context.categoryId,
      category_group_id: context.categoryGroupId,
      active_filters: context.activeFilters,
      current_page: context.currentPage,
      items_loaded: context.itemsLoaded,
      total_items_available: context.totalItemsAvailable,
      properties: loadMoreMemoryProperties(),
    });
  }

  /**
   * Track photo slider interaction
   * Tracks when user swipes/clicks through product photos
   */
  photoSlider(params: {
    listingId: number;
    listingTitle?: string;
    listingPrice?: number;
    categoryId?: number;
    categoryGroupId?: number;
    slideIndex: number;
    totalPhotos: number;
    interactionType?: 'swipe' | 'click' | 'auto';
    sourcePage?: 'product_modal' | 'product_page' | 'store_page';
    searchQuery?: string;
    resultPosition?: number;
  }): void {
    this.track({
      event_type: 'photo_slider',
      listing_id: params.listingId,
      listing_title: params.listingTitle,
      listing_price: params.listingPrice,
      category_id: params.categoryId,
      category_group_id: params.categoryGroupId,
      slide_index: params.slideIndex,
      total_photos: params.totalPhotos,
      interaction_type: params.interactionType,
      source_page: params.sourcePage,
      search_query: params.searchQuery,
      result_position: params.resultPosition,
    });
  }

  /**
   * Track wishlist add/remove (positive interest signal)
   * Strong indicator of purchase intent
   */
  wishlist(params: {
    listingId: number;
    listingTitle?: string;
    listingPrice?: number;
    categoryId?: number;
    categoryGroupId?: number;
    action: 'add' | 'remove';
    sourcePage?: 'product_page' | 'product_modal' | 'search' | 'browse' | 'store_page' | 'similar';
    searchQuery?: string;
    resultPosition?: number;
  }): void {
    this.track({
      event_type: 'wishlist',
      listing_id: params.listingId,
      listing_title: params.listingTitle,
      listing_price: params.listingPrice,
      category_id: params.categoryId,
      category_group_id: params.categoryGroupId,
      action: params.action,
      source_page: params.sourcePage,
      search_query: params.searchQuery,
      result_position: params.resultPosition,
    });
  }

  /**
   * Track "not interested" (negative interest signal)
   * Used for recommendations and personalization
   */
  notInterested(params: {
    listingId: number;
    listingTitle?: string;
    listingPrice?: number;
    categoryId?: number;
    categoryGroupId?: number;
    reason?: 'show_less' | 'show_none' | 'not_relevant' | 'too_expensive' | 'poor_quality' | 'wrong_category' | 'already_purchased' | 'other';
    sourcePage?: 'search' | 'browse' | 'recommendations' | 'similar' | 'for_you';
    searchQuery?: string;
    resultPosition?: number;
  }): void {
    this.track({
      event_type: 'not_interested',
      listing_id: params.listingId,
      listing_title: params.listingTitle,
      listing_price: params.listingPrice,
      category_id: params.categoryId,
      category_group_id: params.categoryGroupId,
      reason: params.reason,
      source_page: params.sourcePage,
      search_query: params.searchQuery,
      result_position: params.resultPosition,
    });
  }

  /**
   * Track search
   */
  search(query: string, resultsCount?: number, filters?: Record<string, string>): void {
    this.track({
      event_type: 'search',
      search_query: query,
      search_results_count: resultsCount,
      search_filters: filters,
    });
  }

  /**
   * Track applied attribute filters (brand, size, colour, gender, age,
   * material). A taste signal: the per-buyer readers fold `tags` into the same
   * affinity vector the ranking uses. See utils/facet-affinity.ts.
   */
  facetSelected(params: {
    searchFilters: Record<string, string>;
    tokens: string[];
    query?: string;
    categoryId?: number;
    categoryGroupId?: number;
  }): void {
    if (params.tokens.length === 0) return;
    this.track({
      event_type: 'filter_facet_selected',
      search_filters: params.searchFilters,
      tags: params.tokens,
      ...(params.query ? { search_query: params.query } : {}),
      ...(params.categoryId ? { category_id: params.categoryId } : {}),
      ...(params.categoryGroupId ? { category_group_id: params.categoryGroupId } : {}),
    });
  }

  /**
   * Track transaction
   */
  transaction(transaction: {
    id: number;
    amount: number;
    status: string;
    listingId?: number;
  }): void {
    this.track({
      event_type: 'transaction',
      transaction_id: transaction.id,
      transaction_amount: transaction.amount,
      transaction_status: transaction.status,
      listing_id: transaction.listingId,
    });
  }

  /**
   * Track chat message
   */
  chatMessage(chatId: number, listingId?: number): void {
    this.track({
      event_type: 'chat_message',
      chat_id: chatId,
      listing_id: listingId,
    });
  }

  /**
   * Track custom event with properties
   */
  custom(eventType: string, properties?: Record<string, string>): void {
    this.track({
      event_type: eventType,
      properties,
    });
  }

  /**
   * Set user ID (call after login)
   */
  identify(userId: number): void {
    try {
      localStorage.setItem('sw_user_id', userId.toString());
    } catch {
      // Blocked storage must not stop the stitch below, which is the durable
      // half -- it is written to ClickHouse, not to this browser.
    }
    // Write down that this browser belongs to this account, so the person's
    // activity from BEFORE they signed in can be attributed to them.
    linkUserToBrowser(userId);
  }

  /**
   * Clear user ID (call after logout)
   */
  reset(): void {

    /**
     * ONLY a real sign-out rotates the visit.
     *
     * `pages/_app.tsx` calls this from an effect on `useSession()` whenever there
     * is no user -- and for a visitor who has never signed in, that is EVERY
     * time the session object's identity changes, which NextAuth does freely on
     * re-render. Rotating unconditionally therefore minted a new `session_id`
     * mid-visit for exactly the population that is hardest to follow already.
     *
     * Measured against the dev server on 2026-09-18 by driving a logged-out
     * browser through four client-side navigations: one browser, one
     * `anonymous_id`, and THREE different session ids -- the first page view,
     * the sell wizard and the return to the home page each filed under a
     * different visit. In production that is a checkout whose start and finish
     * are two sessions, which is part of why checkout funnel sessions matched
     * `events` only 32% of the time.
     *
     * A visitor with no stored user id has nothing to reset, so the correct
     * action is to do nothing. Someone signing OUT still gets a fresh visit,
     * which is the point of the call: their next events must not be filed under
     * the account holder's session.
     */
    let hadUser = false;
    try {
      hadUser = localStorage.getItem('sw_user_id') !== null;
      if (hadUser) localStorage.removeItem('sw_user_id');
    } catch {
      // Blocked storage must not strand a logout -- it already has once here.
    }

    if (!hadUser) return;

    // The session rolls over in ONE place now, so the next event, impression and
    // error all agree about which visit they belong to.
    configureImpressionQueue(startNewSession(), getAnonymousId());
  }

  /**
   * Whether this browser has a signed-in user stored, i.e. whether reset()
   * would have anyone to forget. Blocked storage reads as no user.
   */
  hasUser(): boolean {
    try {
      return localStorage.getItem('sw_user_id') !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get stored user ID
   */
  private getUserId(): number | undefined {
    const userId = localStorage.getItem('sw_user_id');
    return userId ? parseInt(userId, 10) : undefined;
  }

  /**
   * Schedule batch send
   */
  private scheduleBatch(): void {
    if (this.batchTimer) return;

    this.batchTimer = setTimeout(() => {
      void this.flush();
    }, BATCH_INTERVAL_MS);
  }

  /**
   * Flush all queued events immediately.
   *
   * A batch goes by `fetch`, the way the funnel tracker sends; the beacon is for
   * the page going away (`atTeardown`), and the fallback when `fetch` fails.
   *
   * Every flush used to be a beacon, with its result ignored. Some browsers run
   * our pages and never deliver a beacon: on 2026-10-29, 168 checkout visits
   * reached `funnel_events` by `fetch` and left no row at all in `events` -- no
   * page view, no score -- so the funnel counted them as people the visit log
   * had never seen, and the daily pipeline check read 0.80 joinable. A beacon
   * that is refused or dropped says nothing; a `fetch` that fails throws.
   */
  async flush(atTeardown = false): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.eventQueue.length === 0) {
      return;
    }

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    // Add user ID to all events if available
    const userId = this.getUserId();
    if (userId) {
      eventsToSend.forEach(event => {
        event.user_id = userId;
      });
    }

    const body = JSON.stringify({ events: eventsToSend });
    // True when the browser took the batch. A page closing cannot wait for a
    // `fetch`, so teardown tries the beacon first.
    const beacon = (): boolean =>
      typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(API_ENDPOINT, new Blob([body], { type: 'application/json' }));

    if (atTeardown && beacon()) return;

    try {
      await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch (error) {
      // A blocked or offline `fetch` gets the beacon as a second try; it was the
      // only route before, so a visitor it reached is still reached. Logged only
      // when nothing took the batch. Not re-queued, to avoid a loop.
      if (!atTeardown && beacon()) return;
      trackError(error instanceof Error ? error : new Error(String(error)), undefined, { source: 'analytics', action: 'flush' } as ErrorContext);
    }
  }
}

// Lazy singleton - avoids SSR issues where module is evaluated server-side first
let _analyticsInstance: AnalyticsTracker | null = null;

export function getAnalytics(): AnalyticsTracker | null {
  if (typeof window === 'undefined') return null;
  if (!_analyticsInstance) {
    _analyticsInstance = new AnalyticsTracker();
  }
  return _analyticsInstance;
}

/**
 * Reset the analytics singleton - for testing purposes only
 */
export function resetAnalytics(): void {
  _analyticsInstance = null;
}

// For backwards compatibility - but prefer getAnalytics()
export const analytics = typeof window !== 'undefined' ? new AnalyticsTracker() : null;

// Export types
export type { AnalyticsEvent };
